if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');

      // "Columns" layout: "Show more" reveals the images beyond the first 4.
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.product__media-show-more-button');
        if (!button) return;
        const expanded = this.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', expanded);
        button.textContent = expanded ? button.dataset.showLess : button.dataset.showMore;
      });

      if (!this.elements.thumbnails) return;

      this.selectedVariantId = this.dataset.selectedVariantId; // Assuming you set this on the gallery element
      this.firstMediaId = null; // To track the first media for the selected variant

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });

      // "Columns + thumbnails" layout: clicking an overflow thumbnail reveals that
      // image into the 2-column grid (progressive disclosure).
      if (this.dataset.desktopLayout === 'columns_thumbnail') {
        this.elements.thumbnails.addEventListener('click', (event) => {
          const thumbItem = event.target.closest('[data-target]');
          if (!thumbItem) return;
          const cell = this.elements.viewer.querySelector(
            `.product__media-item[data-media-id="${thumbItem.dataset.target}"]`
          );
          if (!cell) return;
          cell.classList.remove('product__media-item--thumb-only');
          cell.classList.add('product__media-item--col');
          thumbItem.classList.add('thumbnail-list__item--hidden');
          this.updateSeeAll();
        });

        // Only 4 overflow thumbnails are shown; "See all N" (N = the rest) moves
        // every remaining overflow image into the grid.
        // Thumbnails of other variants (.thumbnail-list_item--variant after the first)
        // are display:none, so they don't count towards the four or "See all N".
        this.shownThumbs =
          '.thumbnail-list__item:not(.thumbnail-list__item--hidden):not(.thumbnail-list_item--variant:not(:first-child))';
        this.seeAllButton = this.elements.thumbnails.querySelector('.thumbnail-see-all');
        if (this.seeAllButton) {
          this.seeAllButton.addEventListener('click', () => {
            this.elements.thumbnails
              .querySelectorAll(this.shownThumbs)
              .forEach((thumbItem) => {
                const cell = this.elements.viewer.querySelector(
                  `.product__media-item[data-media-id="${thumbItem.dataset.target}"]`
                );
                if (cell) {
                  cell.classList.remove('product__media-item--thumb-only');
                  cell.classList.add('product__media-item--col');
                }
                thumbItem.classList.add('thumbnail-list__item--hidden');
              });
            this.updateSeeAll();
          });
          // Variant changes toggle the hidden class from global.js.
          new MutationObserver(() => this.updateSeeAll()).observe(
            this.elements.thumbnails.querySelector('.thumbnail-list'),
            { attributes: true, attributeFilter: ['class'], subtree: true }
          );
          this.updateSeeAll();
        }
      }

      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    updateSeeAll() {
      const visible = this.elements.thumbnails.querySelectorAll(this.shownThumbs).length;
      const rest = Math.max(visible - 4, 0);
      this.seeAllButton.hidden = rest === 0;
      this.seeAllButton.querySelector('.thumbnail-see-all__count').textContent = rest;
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      // Check if this is the first media item for the selected variant
      const currentMediaVariantId = activeMedia.dataset.variantId; // Assuming each media item has a data attribute for the variant
      if (currentMediaVariantId === this.selectedVariantId) {
        // Check if this is the first media item
        if (!this.firstMediaId) {
          this.firstMediaId = mediaId; // Mark this media as the first for this variant
          this.showBadgeForVariant(this.selectedVariantId); // Show the badge
        }
      }

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    showBadgeForVariant(variantId) {
      const badge = document.querySelector(`.badge[data-variant="${variantId}"]`);
      if (badge) {
        badge.style.display = ''; // Show the badge for this variant
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}